import {ScoreCards} from '../../../../features/dashboard/components/ScoreBoard/ScoreBoardProps';
import {
  AllAvailablePartsStatusResponse,
  BeginShiftRequest,
  GetRequestedPartsData,
  GetRequestedPartsParams,
  BeginShiftResponse,
  EndShiftResponse,
  GetRepairListData,
  GetRepairListParams,
  Pagination,
  PartsProps,
  PartRequestCounts,
  RepairItem,
  RequestedPartItem,
  ShiftStatusIndicatorResponse,
  TechnicianDetails,
  TechnicianDetailsResponse,
  RepairPriorities,
  LastWorkOrderRequest,
} from '../../../../features/dashboard/components/WorkOrders/WorkOrderProps';
import {Props} from '../../../../shared/assets/SvgProps';
import WorkOrderCompletedIcon from '../../../../shared/assets/icons/WorkOrderCompletedIcon';
import WorkOrderAssignedIcon from '../../../../shared/assets/icons/WorkOrderAssignedIcon';
import WorkOrderAwaitingPartsIcon from '../../../../shared/assets/icons/WorkOrderAwaitingPartsIcon';
import {statusChartsList} from '../../../../features/dashboard/components/StatusChart/StatusChartProps';
import {
  leaderBoardColors,
  repairsPriorities,
  scoreChartData,
  workOrderStatus,
  workOrderTabsConst,
} from '../../constants/dashboard.constant';
import {
  LeaderboardItemProps,
  LeaderboardListProps,
} from '../../../../features/dashboard/components/LeaderBoard/LeaderBoardProps';
import {appConstants} from '@shared/constants/global';
import workOrdersData from '../../../../features/dashboard/mocks/workOrders.json';
import {ApiResponse} from '@shared/types/api';
import {apiGet, apiPost} from '@features/auth/api/apiClient';
import {globalErrorHandler} from '@shared/error/GlobalErrorController';
import {endPoints} from '@features/auth/constants/endPoints';
import {parseISO, isValid, getYear} from 'date-fns';
import { DropdownOption } from '@shared/components';

/**
 * This method is used to prepare score board data
 * @param {data}  - Score cards
 * @returns Record<string, number>
 */
export const prepareScoreBoardData = (
  data: ScoreCards[],
): Record<string, number> => {
  return data.reduce((result, scoreCard: ScoreCards) => {
    const {status} = scoreCard;
    const statval = scoreCard?.requestedForParts;
    if (!result[status]) {
      if (statval) {
        result[workOrderTabsConst.awaitingParts] = 0;
        result[status] = 0;
      }
      result[status] = 0;
    }
    if (statval) {
      result[workOrderTabsConst.awaitingParts] =
        (result[workOrderTabsConst.awaitingParts] || 0) + 1;
      status === workOrderTabsConst.active && result[status]++;
    } else {
      result[status]++;
    }
    return result;
  }, {} as Record<string, number>);
};

/**
 * This method is used to prepare score board data
 * @param {data}  - Score cards
 * @returns Record<string, number>
 */
export const prepareRepairBoardData = (
  parts: PartsProps[],
): Record<string, number> => {
  const statusCount = parts.reduce((acc, item: PartsProps) => {
    acc[item.status] = (acc[item.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  console.log('statusCount', statusCount);
  return statusCount;
};

/**
 * This method is used to prepare score board data
 * @param {data}  - Score cards
 * @returns Record<string, number>
 */
export const prepareScoreData = (
  data: ScoreCards[],
): Record<string, number> => {
  return data.reduce((result, scoreCard: ScoreCards) => {
    const {status} = scoreCard;
    if (!result[status]) {
      result[status] = 0;
    }
    result[status]++;
    return result;
  }, {} as Record<string, number>);
};

/**
 * This method is used to get the filtered work ordersnlist
 * @param {data} - WOrk orders
 * @param {filterValue} - Filter value
 * @returns {WorkOrderCards[]}
 */
export const getFilteredWorkOrders = (
  data: RepairItem[],
  filterValue: string,
): RepairItem[] => {
  switch (filterValue) {
    case workOrderTabsConst.open:
      return data.filter((workOrderData: RepairItem) => {
        const status = workOrderData.status?.toLowerCase();
        return (
          status === workOrderTabsConst.open.toLowerCase() ||
          status === workOrderTabsConst.assigned.toLowerCase() ||
          status === workOrderTabsConst.working.toLowerCase() ||
          status === workOrderTabsConst.pending.toLowerCase()
        );
      });
    case workOrderTabsConst.active:
      return data.filter(
        (workOrderData: RepairItem) =>
          workOrderData.repairStatus === workOrderTabsConst.active,
      );
    case workOrderTabsConst.assigned:
      return data.filter(
        (workOrderData: RepairItem) =>
          workOrderData.repairStatus === workOrderTabsConst.assigned,
      );
    case workOrderTabsConst.completed:
      return data.filter(
        (workOrderData: RepairItem) =>
          workOrderData.status?.toLowerCase() ===
            workOrderTabsConst?.completed?.toLowerCase() ||
          workOrderData.repairStatus?.toLowerCase() ===
            workOrderTabsConst?.completed?.toLowerCase(),
      );
    case workOrderTabsConst.previousWO:
      return data.filter(
        (workOrderData: RepairItem) =>
          (workOrderData.status?.toLowerCase() ===
            workOrderTabsConst?.completed?.toLowerCase() ||
            workOrderData.repairStatus?.toLowerCase() ===
              workOrderTabsConst?.completed?.toLowerCase()) &&
          new Date(workOrderData.inDate) < new Date(),
      );
    default:
      return data;
  }
};

export const getRepairCardButtonText = (status: string): string => {
  if (status?.toUpperCase() === workOrderTabsConst?.working?.toUpperCase()) {
    return 'dashboard.workOrders.resume';
  } else if (
    status?.toUpperCase() === workOrderTabsConst?.completed?.toUpperCase()
  ) {
    return 'dashboard.workOrders.closed';
  } else {
    return 'dashboard.workOrders.begin';
  }
};

/**
 * This method is used to get the filtered work ordersnlist
 * @param {data} - WOrk orders
 * @param {filterValue} - Filter value
 * @returns {WorkOrderCards[]}
 */
export const getFilteredParts = (
  data: PartsProps[],
  filterValue: string,
): PartsProps[] => {
  switch (filterValue) {
    case workOrderTabsConst.all:
      return [...data];
    case workOrderTabsConst.activeRequest:
      return data.filter(
        (workOrderData: PartsProps) =>
          workOrderData.status === workOrderTabsConst.requestedParts ||
          workOrderData.status === workOrderTabsConst.delayedParts,
      );
    case workOrderTabsConst.pastRequests:
      return data.filter(
        (workOrderData: PartsProps) =>
          workOrderData.status === workOrderTabsConst.issuedParts,
      );
  }
  return [];
};

/**
 * This method is used to filter repairs count
 * @param {open} - Open count
 * @param {closed} - Closed count
 * @returns {object}
 */
export const getFilteredRepairsCount = (
  open: number,
  closed: number,
): Record<string, number> => {
  return {
    All: open + closed,
    Open: open,
    Complete: closed,
  };
};

export const getWorkOrderCounts = (
  data: RepairItem[],
): Record<string, number> => {
  const today = new Date();

  return data.reduce(
    (acc, order) => {
      const status = order.status?.toLowerCase();
      const dueDate = new Date(order.inDate);
      acc.All = (acc.All || 0) + 1;

      if (
        status === workOrderTabsConst?.open?.toLowerCase() ||
        status === workOrderTabsConst?.assigned?.toLowerCase() ||
        status === workOrderTabsConst?.working?.toLowerCase() ||
        status === workOrderTabsConst.pending.toLowerCase()
      ) {
        acc.Open = (acc.Open || 0) + 1;
      }

      if (
        status === workOrderTabsConst?.completed?.toLowerCase() ||
        order.repairStatus?.toLowerCase() ===
          workOrderTabsConst?.completed?.toLowerCase()
      ) {
        acc.Complete = (acc.Complete || 0) + 1;

        // Previous work orders = completed but past due date
        if (dueDate < today) {
          acc['Previous WorkOrders'] = (acc['Previous WorkOrders'] || 0) + 1;
        }
      }

      return acc;
    },
    {Open: 0, Complete: 0, 'Previous WorkOrders': 0} as Record<string, number>,
  );
};

export const getPartsCounts = (data: Array<{status?: string}>) => {
  console.log('data', data);

  return data.reduce(
    (acc, order) => {
      const status = order.status?.toLowerCase();
      acc['All'] += 1;

      if (
        status === workOrderTabsConst?.requestedParts?.toLowerCase() ||
        status === workOrderTabsConst?.delayedParts?.toLowerCase()
      ) {
        acc['Active Requests'] += 1;
      }

      if (status === workOrderTabsConst?.issuedParts?.toLowerCase()) {
        acc['Past Requests'] += 1;
      }

      return acc;
    },
    {All: 0, 'Active Requests': 0, 'Past Requests': 0},
  );
};

/**
 * This method is used to get the status icon
 * @param {status} - string
 * @returns {React.FC<Props> | null}
 */
export const getStatusIcon = (status: string): React.FC<Props> | null => {
  switch (status) {
    case workOrderTabsConst.awaitingParts:
      return WorkOrderAwaitingPartsIcon;
    case workOrderTabsConst.assigned:
      return WorkOrderAssignedIcon;
    case workOrderTabsConst.completed:
      return WorkOrderCompletedIcon;
    default:
      return null;
  }
};

/**
 * This method is used to prepare score chart data
 * @param {data}  - Score cards
 * @returns {statusChartsList[]}
 */
export const prepareStatusChartData = (
  data: ScoreCards[],
): statusChartsList[] => {
  const countList = prepareScoreBoardData(data);
  return scoreChartData?.map(chartData => {
    return {
      title: chartData.title,
      count: countList[chartData.key] || 0,
      color: chartData.color,
    };
  });
};

/**
 * This method is used to give the formatted date range
 * @param {fromDate} - string
 * @param {toDate} - string
 * @returns {string}
 */
export const getFormattedDateRange = (fromDate: string, toDate: string) => {
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const month = date.toLocaleDateString('en-US', {month: 'short'});
    const day = date.getDate();
    return `${month} ${day}`;
  };

  const formattedFrom = formatDate(fromDate);
  const formattedTo = formatDate(toDate);

  if (formattedFrom && formattedTo) {
    return `${formattedFrom} - ${formattedTo}`;
  }
  return '';
};

export const getLeaderBoardData = (
  data: LeaderboardItemProps[],
): LeaderboardListProps[] => {
  // sort by repairs count
  const sorted = [...data].sort((a, b) => b.repairs - a.repairs);

  let lastRepairs: number | null = null;
  let lastRank = 0;

  return sorted.map((item, index) => {
    if (item.repairs !== lastRepairs) {
      lastRank = index + 1;
      lastRepairs = item.repairs;
    }

    return {
      rank: lastRank,
      name: item.name,
      repairs: item.repairs,
      id: item.id,
      iconStyles:
        leaderBoardColors?.[(index + 1) as keyof typeof leaderBoardColors],
    };
  });
};

export const getRepairsList = async ({
  technicianId = '',
  page = 1,
  pageSize = 6,
  sortBy = '',
  sortOrder = '',
  shopId = '',
  statusFilter = 'All',
}: GetRepairListParams): Promise<GetRepairListData | undefined> => {
  try {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      sortBy: sortBy,
      sortOrder: sortOrder, // 'asc', //sortOrder
      shopId: shopId.toString(),
      technicianId: technicianId.toString(),
      statusFilter: statusFilter,
    });
    let response: ApiResponse<GetRepairListData>;
    if (appConstants.isMock) {
      return workOrdersData.data as unknown as GetRepairListData;
    }
    const {data} = await apiGet<GetRepairListData>({
      endpoint: `${endPoints.dashboard.getWorkOrders}${queryParams.toString()}`,
    });
    response = data;

    if (response?.success && response?.data) {
      console.log('response from', response.data);
      return response?.data;
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

export const getFilteredRepairsList = async ({
  technicianId,
  page = 1,
  pageSize = 6,
  sortBy = '',
  sortOrder = '',
  shopId = '',
  searchText = '',
  searchValue = '',
  statusFilter = 'All',
}: GetRepairListParams): Promise<GetRepairListData | undefined> => {
  try {
    let payload: Record<string, string> = {
      page: page.toString(),
      pageSize: pageSize.toString(),
      sortBy: sortBy,
      sortOrder: sortOrder,
      searchText: searchText,
      searchValue: searchValue,
      statusFilter: statusFilter,
    };
    if (shopId) {
      payload.shopId = shopId.toString();
    }

    if (technicianId) {
      payload.technicianId = technicianId.toString();
    }
    let response: ApiResponse<GetRepairListData>;
    if (appConstants.isMock) {
      return workOrdersData.data as unknown as GetRepairListData;
    }
    const {data} = await apiPost<GetRepairListData>({
      endpoint: `${endPoints.dashboard.filterWorkOrders}`,
      payload,
    });
    response = data;
    console.log('response from', response);
    if (response?.success && response?.data) {
      console.log('response from', response);
      return response?.data;
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to fetch technician details
 * @param {technicianId} - number
 * @returns {Promise<TechnicianDetails | undefined>}
 */
export const getTechnicianDetails = async (
  technicianId: number,
): Promise<TechnicianDetailsResponse | undefined> => {
  try {
    const response = await apiGet<TechnicianDetails>({
      endpoint: `${endPoints.dashboard.getTechnicianDetails}${technicianId}`,
    });

    // The actual API response is: { success, data: TechnicianDetails, shiftBeginTime }
    // shiftBeginTime lives alongside `data` in the ApiResponse body, not nested inside it
    const responseBody = response.data as ApiResponse<TechnicianDetails> & {
      shiftBeginTime?: string;
    };

    if (responseBody?.data) {
      return {
        data: responseBody.data,
        shiftBeginTime: responseBody.shiftBeginTime ?? '',
      };
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to get the technician status
 * @param {startTime}
 * @package {endTime}
 */
export const getTechnicianStatus = (
  startTime: string,
  endTime: string,
): boolean => {
  const isValidDate = (dateStr: string) => {
    // 1. Check for empty or known junk strings
    if (!dateStr || dateStr.includes('00-00-')) return false;

    // 2. Parse as ISO 8601 and verify it is a real date with a positive year
    const parsed = parseISO(dateStr);
    return isValid(parsed) && getYear(parsed) > 0;
  };

  const hasStart = isValidDate(startTime);
  const hasEnd = isValidDate(endTime);

  // If both empty OR both present, return false (Not in shift)
  if ((!hasStart && !hasEnd) || (hasStart && hasEnd)) {
    return false;
  }

  // If only start exists, return true (In shift)
  if (hasStart && !hasEnd) {
    return true;
  }

  return false;
};

export const getPriorityValue = (value: string) => {
  switch (value) {
    case 'ASAP':
      return repairsPriorities?.normal;
    case 'UR':
      return repairsPriorities?.critical;
    case 'RED':
      return repairsPriorities?.emergency;
    case 'WP':
      return repairsPriorities?.low;
  }
};

export const beginShift = async ({
  technicianId,
  shopId,
  createdUserId,
  userTimeZone,
  continueInOvertime = false,
}: BeginShiftRequest): Promise<BeginShiftResponse | undefined> => {
  try {
    const payload = {
      technicianId,
      shopId,
      createdUserID: createdUserId,
      continueInOvertime,
      userTimeZone: userTimeZone || '',
    };
    let response: ApiResponse<BeginShiftResponse>;

    const {data} = await apiPost<BeginShiftResponse>({
      endpoint: endPoints?.dashboard?.beginShift,
      payload,
    });
    response = data;

    if (response?.data) {
      return response?.data;
    } else if (response?.message) {
      // API returned no data but included a message (e.g. session-expired)
      return {message: response.message} as BeginShiftResponse;
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

export const endShift = async (
  technicianId: number,
  IsLaborOvertimeCheckRequired: boolean,
  isIndirectOvertimeCheckRequired: boolean,
  userTimeZone: string
): Promise<EndShiftResponse | undefined> => {
  try {
    let response: ApiResponse<EndShiftResponse>;
    const {data} = await apiGet<EndShiftResponse>({
      endpoint: endPoints?.dashboard?.endShift(technicianId, IsLaborOvertimeCheckRequired, isIndirectOvertimeCheckRequired, userTimeZone)
      // endpoint: endPoints?.dashboard?.endShift(technicianId, false, false, userTimeZone)
    });
    response = data;

    if (response) {
      return response;
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

export const getCompletedRepairsCount = async (
  technicianId: number,
  shopId: string,
): Promise<string | undefined> => {
  try {
    let response: ApiResponse<string>;
    const queryParams = new URLSearchParams({
      shopId: shopId.toString(),
    });
    const {data} = await apiGet<string>({
      endpoint: `${endPoints?.dashboard?.completedRepairsCount(
        technicianId,
      )}${queryParams}`,
    });
    response = data;

    if (response?.data) {
      return response?.data;
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * Fetches the shift status indicator for a technician
 * @param {technicianId} - number
 * @returns {Promise<ShiftStatusIndicatorResponse | undefined>}
 */
export const getShiftStatus = async (
  technicianId: number,
): Promise<ShiftStatusIndicatorResponse | undefined> => {
  try {
    const {data} = await apiGet<ShiftStatusIndicatorResponse>({
      endpoint: endPoints.dashboard.shiftStatus(technicianId),
    });
    const response = data as ApiResponse<ShiftStatusIndicatorResponse>;
    if (response?.data) {
      return response.data;
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * Fetches the shift status indicator for a technician
 * @returns {Promise<AllAvailablePartsStatusResponse | undefined>}
 */
export const getAllAvailablePartsStatus = async (): Promise<
  AllAvailablePartsStatusResponse[] | undefined
> => {
  try {
    const {data} = await apiGet<AllAvailablePartsStatusResponse[]>({
      endpoint: endPoints.dashboard.allAvailablePartsStatus,
    });
    const response = data as ApiResponse<AllAvailablePartsStatusResponse[]>;
    if (response?.data) {
      return response.data;
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * Fetches the list of requested parts for a technician
 * @param {GetRequestedPartsParams} params - Query parameters
 * @returns {Promise<GetRequestedPartsData | undefined>}
 */
export const getRequestedParts = async ({
  technicianId,
  requestedPartStatusId,
  isRequestActive,
  pageNumber = 1,
  pageSize = 6,
  sortBy,
  isAscending,
}: GetRequestedPartsParams): Promise<GetRequestedPartsData | undefined> => {
  try {
    // Build the POST body — only include optional fields when they have values
    // so the API doesn't receive null/undefined for fields it expects to be absent.
    const payload: Record<string, unknown> = {
      technicianId,
      pageNumber,
      pageSize,
    };

    if (requestedPartStatusId !== undefined) {
      payload.requestedPartStatusId = requestedPartStatusId;
    }
    if (isRequestActive !== undefined) {
      payload.isRequestActive = isRequestActive;
    }
    // Only include sort params when the user has explicitly chosen a sort column.
    if (sortBy) {
      payload.sortBy = sortBy;
    }
    if (isAscending !== undefined) {
      payload.isAscending = isAscending;
    }

    const {data} = await apiPost<GetRequestedPartsData>({
      endpoint: endPoints.dashboard.requestedParts,
      payload,
    });
    const response = data as ApiResponse<GetRequestedPartsData>;
    if (response?.data) {
      return {
        data: response.data.data,
        pagination: response.data.pagination ?? {
          currentPage: pageNumber,
          pageSize,
          totalItems: 0,
          totalPages: 0,
          hasNextPage: false,
          hasPreviousPage: false,
        },
      };
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to prepare the dropdown values to show the parts status filters
 * @param {statuses} - AllAvailablePartsStatusResponse
 */
export const preparePartsStatusesFilters = (
  statuses: AllAvailablePartsStatusResponse[],
  fromDashboard?: boolean
): DropdownOption[] => {
  const val = (statuses ?? [])
    .map((stores: AllAvailablePartsStatusResponse) => {
      if (stores?.requestPartStatus === workOrderTabsConst?.issuedParts) {
        return undefined;
      }
      return {
        label: stores?.requestPartStatus,
        value: stores?.requestPartStatusId,
      } as DropdownOption;
    })
    .filter((item): item is DropdownOption => item !== undefined);
  return fromDashboard ? [...val] : [{label: 'All', value: ' '}, ...val];
};

/**
 * Fetches all the priorities of the repair
 * @returns {Promise<RepairPriorities | undefined>}
 */
export const getAllRepairPriorities = async (): Promise<
  RepairPriorities[] | undefined
> => {
  try {
    const {data} = await apiGet<RepairPriorities[]>({
      endpoint: endPoints.dashboard.allRepairPriorities,
    });
    const response = data as ApiResponse<RepairPriorities[]>;
    if (response?.data) {
      return response.data;
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * Fetches the part request counts (requested / reserved / onOrder) for a technician
 * @param {technicianId} - number
 * @returns {Promise<PartRequestCounts | undefined>}
 */
export const getPartRequestCounts = async (
  technicianId: number,
): Promise<PartRequestCounts | undefined> => {
  try {
    const {data} = await apiGet<PartRequestCounts>({
      endpoint: endPoints.dashboard.partRequestCounts(technicianId),
    });
    const response = data as ApiResponse<PartRequestCounts>;
    if (response?.data) {
      return response.data;
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};

/**
 * This method is used to get the last work orders
 * @param {LastWorkOrderRequest} - technicianId, page, pageSize
 * @returns {}
 */
export const getLastWorkOrders = async ({
  technicianId,
  page,
  pageSize,
}: LastWorkOrderRequest): Promise<GetRepairListData | string | undefined> => {
  try {
    const {data} = await apiGet<GetRepairListData>({
      endpoint: `${endPoints.dashboard.getLastWOList(technicianId)}&page=${page}&pageSize=${pageSize}`,
    });
    const response = data as ApiResponse<GetRepairListData | string>;
    if (response?.data) {
      return response.data;
    } else if (response?.message) {
      return response?.message;
    }
  } catch (error: any) {
    globalErrorHandler(error);
    throw error;
  }
};